import { Button } from '@avanta/shared/components/ui/Button';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { CalendarCog, FileText, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router';
import { ROUTES } from 'constants/routes';
import { type AppointmentWithDetails } from 'types/appointments';
import { useViewSessionReport } from './useViewSessionReport';

const ManageSessionAction = ({ patientId }: { patientId: string }): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const navigate = useNavigate();

  return (
    <Button
      variant="terciary"
      icon="leading"
      onClick={() =>
        navigate(generatePath(ROUTES.PATIENT_APPOINTMENT, { id: patientId }), {
          state: { from: ROUTES.APPOINTMENTS },
        })
      }
    >
      <CalendarCog />
      {t('SessionsPage.actions.manageSession')}
    </Button>
  );
};

const SessionDetailsAction = ({ patientId }: { patientId: string }): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const navigate = useNavigate();

  return (
    <Button
      variant="terciary"
      icon="leading"
      onClick={() =>
        navigate(generatePath(ROUTES.PATIENT_APPOINTMENT, { id: patientId }), {
          state: { from: ROUTES.APPOINTMENTS },
        })
      }
    >
      <Info />
      {t('SessionsPage.actions.sessionDetails')}
    </Button>
  );
};

const ViewReportAction = ({
  appointment,
}: {
  appointment: AppointmentWithDetails;
}): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const { viewReport, isPending } = useViewSessionReport(appointment);

  return (
    <Button variant="terciary" icon="leading" isLoading={isPending} onClick={viewReport}>
      <FileText />
      {t('SessionsPage.actions.viewReport')}
    </Button>
  );
};

const NoAction = (): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);

  return <span className="text-gray-400">{t('SessionsPage.actions.none')}</span>;
};

export const TableActions = ({
  appointment,
}: {
  appointment: AppointmentWithDetails;
}): React.ReactElement => {
  if (appointment.canceled) {
    return <SessionDetailsAction patientId={appointment.patientId} />;
  }

  if (new Date(appointment.dateTime) <= new Date()) {
    return appointment.id ? <ViewReportAction appointment={appointment} /> : <NoAction />;
  }

  return <ManageSessionAction patientId={appointment.patientId} />;
};
